import styles from './index.module.scss';
import { utility, Dialog } from '@phi/components';
import { Flex, Section, Sections } from '@phi/components/layout';
import {
    hide_settings_modal,
    showing_settings_modal,
} from '@features/user-settings/state';
import { PropType, defineComponent, h } from 'vue';
import { ShowHideLabels } from '../show-hide-labels';
import { ExpandedLayout } from '../expanded-layout';
import { ShowHideTooltips } from '../show-hide-tooltips';
import { BackgroundPicker } from '../background';
import { EnvironmentPicker } from '../environment';
import { LightingPicker } from '../lighting';
import { ZoomSensitivity } from '../zoom-sensitivity';
import { InsideRed } from '../inside-red';
import { ReferencesTransparent } from '../references-transparent';
import { DisplayTriangles } from '../display-triangles';
import { LengthUnit } from '../length-unit';
import { CoordinateDecimals } from '../coordinate-decimals';
import { SplitEdges } from '../split-edges';
import { SaveBackups } from '../save-backups';
import { ResetSettings } from '../reset-settings';
import { FindDiscontinuities } from '../find-discontinuities';
import { AngleThreshold } from '../angle-threshold';
import { CheckQuality } from '../check-quality';
import { InterruptDragOnIssue } from '../interrupt-drag-on-issue';
import { CheckNow } from '../check-now';
import { IFrontendServiceContainer } from '@phi/frontend/service-container';
import { CookieConsentProvider, CookieConsentSetting, ICookieConsentProvisions } from '@phi/analytics';

export const SettingsModal = defineComponent({
    props: {
        app: {
            required: true,
            type: Object as PropType<IFrontendServiceContainer>,
        }
    },

    setup(props) {
        return () => h(Dialog, {
            title: 'Settings',
            id: 'settings-modal',
            show: showing_settings_modal.value,
            'onUpdate:show': x => showing_settings_modal.value = x,
            onClose: hide_settings_modal,
            backdrop: false,
            confirm_label: null,
            width: 'narrower',
        }, () => h(Sections, {
            gap: 'extra-wide',
            separator: 'border',
            class: styles.sections,
        }, () => [
            h(Section, null, {
                header: () => h('header', { class: [utility.big] }, 'Appearance'),
                body: () => h(Flex, { gap: 'wide' }, () => [
                    ShowHideLabels(),
                    ExpandedLayout(),
                    ShowHideTooltips(),
                    BackgroundPicker(),
                    EnvironmentPicker(),
                    LightingPicker(),
                ]),
            }),

            h(Section, null, {
                header: () => h('header', { class: [utility.big] }, 'Behavior'),
                body: () => h(Flex, { gap: 'wide' }, () => [
                    ZoomSensitivity(),
                ]),
            }),

            h(Section, null, {
                header: () => h('header', { class: [utility.big] }, 'Geometry'),
                body: () => h(Flex, { gap: 'wide' }, () => [
                    DisplayTriangles(),
                    InsideRed(),
                    ReferencesTransparent(),
                ]),
            }),

            h(Section, null, {
                header: () => h('header', { class: [utility.big] }, 'Indicators'),
                body: () => h(Flex, { gap: 'wide' }, () => [
                    LengthUnit(),
                    CoordinateDecimals(),
                ]),
            }),

            h(Section, null, {
                header: () => h('header', { class: [utility.big] }, 'Files'),
                body: () => h(Flex, { gap: 'wide' }, () => [
                    SplitEdges(),
                    SaveBackups(),
                ]),
            }),

            h(Section, null, {
                header: () => h('header', { class: [utility.big] }, 'Privacy'),
                body: () => h(Flex, { gap: 'wide' }, () => [
                    h(CookieConsentProvider, { analytics: props.app.analytics }, {
                        default: (provisions: ICookieConsentProvisions) =>
                            h(CookieConsentSetting, {
                                consent: provisions.consent.value,
                                'onUpdate:consent': provisions.updateConsent,
                            })
                    })
                ]),
            }),

            h(Section, null, {
                header: () => h('header', { class: [utility.big] }, 'Consistency checks'),
                body: () => h(Flex, { gap: 'wide' }, () => [
                    FindDiscontinuities(),
                    AngleThreshold(),
                    CheckQuality(),
                    InterruptDragOnIssue(),
                    CheckNow(),
                ]),
            }),

            h(Section, null, {
                header: () => h('header', { class: [utility.big] }, 'Miscellaneous'),
                body: () => h(Flex, { gap: 'wide' }, () => [ ResetSettings() ]),
            }),
        ]));
    },
});
